import { GROUP_TYPE, IGroupChatData } from "@home-health-notify/domain";
import Box from "@mui/material/Box";
import { useTranslation } from "hhn-commons";
import React from "react";
import EmptyRecordView from "../../common/EmptyRecordView";
import GoToTop from "../../common/GoToTop";
import ScrollListContainer from "../../common/ScrollListContainer";
import { useScrollAnchor } from "../../hooks/useScrollAnchor";
import ProgressLoader from "../../loader/ProgressLoader";
import InternalGroupChatListItem from "../group/internal/InternalGroupChatListItem";
import PatientGroupChatListItem from "../group/patient/PatientGroupChatListItem";
import { CHAT_DETAILS } from "../group/patient/PatientChats";

interface HiddenGroupChatListProps {
  chats: IGroupChatData[];
  selectedChatId?: string;
  fetchChatList: () => Promise<void>;
  isMoreChats: boolean;
  isChatLoading: boolean;
}

const HiddenGroupChatList = ({
  selectedChatId,
  fetchChatList,
  isChatLoading,
  chats,
  isMoreChats,
}: HiddenGroupChatListProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const { scrollContainerRef, isShowGoToTopButton, goToTop } = useScrollAnchor({
    items: chats,
    getItemId: chat => chat.groupInfo.id,
    getItemVersion: chat => chat.updatedAt,
    selectedItemId: selectedChatId,
  });

  return (
    <ScrollListContainer
      ref={scrollContainerRef}
      isLoading={isChatLoading}
      dataTestId="hiddenGroupChatListComponent"
      onScrollCallback={fetchChatList}
      restTop={isChatLoading ? 188 : 166}
    >
      <>
        {isShowGoToTopButton && <GoToTop onClick={goToTop} />}
        {chats.length > 0 && (
          <Box data-testid="hidden-groups-chatList">
            {chats.map(chatData => {
              const groupType = chatData.groupType;
              const isSelected = selectedChatId === chatData.groupInfo.id;
              return (
                <React.Fragment key={chatData.groupInfo.id}>
                  {groupType === GROUP_TYPE.PATIENT && (
                    <PatientGroupChatListItem
                      chatData={chatData}
                      isSelected={isSelected}
                      isHidden={true}
                      showChatDetails={CHAT_DETAILS.COMPACT}
                    />
                  )}
                  {groupType === GROUP_TYPE.NON_PATIENT && (
                    <InternalGroupChatListItem
                      chatData={chatData}
                      isSelected={isSelected}
                      isHidden={true}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
        {!isChatLoading && !isMoreChats && chats.length !== 0 && (
          <EmptyRecordView emptyMessage={t("endOfTheListMessage")} />
        )}
        {!isChatLoading && chats.length === 0 && <EmptyRecordView />}
        {isChatLoading && (
          <ProgressLoader
            label={tNs("chatLoadingMessage")}
            style={{
              height: "auto",
            }}
          />
        )}
      </>
    </ScrollListContainer>
  );
};

export default HiddenGroupChatList;
